import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import {
  Modal,
  Button,
  Typography,
  Spin,
  List,
  Space,
  message,
  Image,
  Alert,
  Table,
  Select,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import styled from "@emotion/styled";
import { Global, css } from "@emotion/react";
import { getOrderDetail, confirmManualPaymentBatch } from "services/ordersApi";
import type { GetOrderDetailData } from "services/interface";
import { useQueryClient } from "@tanstack/react-query";
import noImage from "assets/images/no-image.png";
import { useAppSelector } from "store";
import { FileExcelOutlined, FilePdfOutlined } from "@ant-design/icons";
import type {
  BulkAcceptProductGroup,
  BulkAcceptSkuLine,
  SkuSaleStatus,
} from "./bulkAcceptExport/types";
import { buildBulkAcceptExportPayload } from "./bulkAcceptExport/buildBulkAcceptExportPayload";
import { exportBulkAcceptSummaryExcel } from "./bulkAcceptExport/exportBulkAcceptSummaryExcel";
import { createBulkAcceptSummaryPdf } from "./bulkAcceptExport/createBulkAcceptSummaryPdf";
import {
  BULK_ACCEPT_QTY_SUMMARY_GRID_STYLE,
  CodCreditInfoBulk,
} from "components/OrderManage/shared/CodCreditInfo";
import { useFactoryCodCreditInfo } from "hooks/useFactoryCredit";

export type { BulkAcceptProductGroup, BulkAcceptSkuLine } from "./bulkAcceptExport/types";

/** ต้องสอดคล้องกับ PAYMENT_MANUAL_ORDER_BATCH_MAX ใน bangbow-api-v2 `payment-manual-order-batch.dto.ts` */
const BATCH_CONFIRM_MAX = 80;

type SummaryTableRow = {
  key: string;
  isProduct: boolean;
  rankDisplay: string;
  image?: string;
  nameText: string;
  qtyDisplay: string;
};

interface BulkAcceptNewOrdersModalProps {
  open: boolean;
  onClose: () => void;
  orderIds: string[];
  factoryId: string;
  onSuccess: () => void;
}

const DETAIL_CHUNK = 40;

/** ขนาดตัวอักษรโมดัล เท่ากับ datatable จริง (OrderTableList ใช้ font-size default ของ antd = 14px)
 * (ยกเว้น dropdown สถานะ SKU = 11px) — ไม่ใช้ตัวหนา */
const BULK_ACCEPT_SUMMARY_FONT_SIZE = 14;

/** สีพื้นหลังตัวเลือก &quot;ไม่พร้อมจำหน่าย&quot; — ให้สอดคล้อง danger ใน PDF/export */
const SKU_STATUS_UNAVAILABLE_BG = "#f5222d";
const SKU_STATUS_UNAVAILABLE_HOVER_BG = "#cf1322";
/** ตัวอักษรบนพื้นแดง */
const SKU_STATUS_UNAVAILABLE_TEXT = "#ffffff";

const SKU_STATUS_OPTIONS: { value: SkuSaleStatus; label: string; className?: string }[] = [
  { value: "available", label: "พร้อมจำหน่าย" },
  {
    value: "unavailable",
    label: "ไม่พร้อมจำหน่าย",
    className: "bulk-accept-sku-status-unavailable-option",
  },
];

const SKU_STATUS_SELECT_POPUP_CLASS = "bulk-accept-sku-status-dropdown";
const BULK_ACCEPT_DETAIL_TABLE_CLASS = "bulk-accept-detail-table";
const BULK_ACCEPT_MODAL_ROOT_CLASS = "bulk-accept-modal-root";

/** Select สถานะ SKU — ตัวอักษร 11px ทั้งที่เลือกและในเมนู dropdown */
const SkuStatusSelect = styled(Select)`
  &.ant-select-single.ant-select-sm .ant-select-selector {
    font-size: 11px;
    min-height: 22px;
    height: 22px;
    padding-inline: 6px;
  }
  &.ant-select-single.ant-select-sm .ant-select-selection-item,
  &.ant-select-single.ant-select-sm .ant-select-selection-placeholder {
    font-size: 11px;
    line-height: 20px;
  }
`;

const bulkAcceptModalGlobalStyles = css`
  .${SKU_STATUS_SELECT_POPUP_CLASS} .ant-select-item,
  .${SKU_STATUS_SELECT_POPUP_CLASS} .ant-select-item-option-content {
    font-size: 11px;
    line-height: 20px;
  }
  .${SKU_STATUS_SELECT_POPUP_CLASS} .ant-select-item {
    min-height: 24px;
    padding: 2px 8px;
  }
  .${SKU_STATUS_SELECT_POPUP_CLASS}
    .bulk-accept-sku-status-unavailable-option.ant-select-item-option {
    background-color: ${SKU_STATUS_UNAVAILABLE_BG} !important;
  }
  .${SKU_STATUS_SELECT_POPUP_CLASS}
    .bulk-accept-sku-status-unavailable-option:not(.ant-select-item-option-disabled).ant-select-item-option-active,
  .${SKU_STATUS_SELECT_POPUP_CLASS}
    .bulk-accept-sku-status-unavailable-option:not(.ant-select-item-option-disabled).ant-select-item-option-selected {
    background-color: ${SKU_STATUS_UNAVAILABLE_HOVER_BG} !important;
  }
  .${SKU_STATUS_SELECT_POPUP_CLASS}
    .bulk-accept-sku-status-unavailable-option
    .ant-select-item-option-content {
    color: ${SKU_STATUS_UNAVAILABLE_TEXT} !important;
  }
  .bulk-accept-sku-status-select-when-unavailable.ant-select .ant-select-selector {
    background-color: ${SKU_STATUS_UNAVAILABLE_BG} !important;
    border-color: ${SKU_STATUS_UNAVAILABLE_BG} !important;
  }
  .bulk-accept-sku-status-select-when-unavailable.ant-select:not(.ant-select-disabled):hover .ant-select-selector,
  .bulk-accept-sku-status-select-when-unavailable.ant-select.ant-select-focused:not(.ant-select-disabled) .ant-select-selector {
    border-color: ${SKU_STATUS_UNAVAILABLE_HOVER_BG} !important;
  }
  .bulk-accept-sku-status-select-when-unavailable .ant-select-selection-item,
  .bulk-accept-sku-status-select-when-unavailable .ant-select-arrow,
  .bulk-accept-sku-status-select-when-unavailable .ant-select-arrow .anticon {
    color: ${SKU_STATUS_UNAVAILABLE_TEXT} !important;
  }
  .${BULK_ACCEPT_MODAL_ROOT_CLASS} .ant-modal-body {
    font-size: ${BULK_ACCEPT_SUMMARY_FONT_SIZE}px;
  }
  .${BULK_ACCEPT_MODAL_ROOT_CLASS} .ant-modal-title {
    font-size: ${BULK_ACCEPT_SUMMARY_FONT_SIZE}px;
    font-weight: 400;
  }
  .${BULK_ACCEPT_MODAL_ROOT_CLASS} .ant-modal-footer .ant-btn {
    font-size: ${BULK_ACCEPT_SUMMARY_FONT_SIZE}px;
    font-weight: 400;
  }
  .${BULK_ACCEPT_MODAL_ROOT_CLASS} .ant-typography {
    font-size: inherit;
    font-weight: 400;
  }
  .${BULK_ACCEPT_MODAL_ROOT_CLASS} .ant-alert-message,
  .${BULK_ACCEPT_MODAL_ROOT_CLASS} .ant-alert-description,
  .${BULK_ACCEPT_MODAL_ROOT_CLASS} .ant-list,
  .${BULK_ACCEPT_MODAL_ROOT_CLASS} .ant-list-item,
  .${BULK_ACCEPT_MODAL_ROOT_CLASS} .ant-spin-text,
  .${BULK_ACCEPT_MODAL_ROOT_CLASS} .ant-empty-description {
    font-size: ${BULK_ACCEPT_SUMMARY_FONT_SIZE}px;
    font-weight: 400;
  }
  .${BULK_ACCEPT_DETAIL_TABLE_CLASS} .ant-table,
  .${BULK_ACCEPT_DETAIL_TABLE_CLASS} .ant-table-cell {
    font-size: ${BULK_ACCEPT_SUMMARY_FONT_SIZE}px;
    font-weight: 400;
  }
  .${BULK_ACCEPT_DETAIL_TABLE_CLASS} .ant-table-thead > tr > th,
  .${BULK_ACCEPT_DETAIL_TABLE_CLASS} .ant-table-thead > tr > th .ant-table-column-title {
    font-weight: 400 !important;
  }
`;

function buildDefaultSkuAvailability(groups: BulkAcceptProductGroup[]): Record<string, SkuSaleStatus> {
  const next: Record<string, SkuSaleStatus> = {};
  for (const g of groups) {
    for (const s of g.skus) {
      next[s.key] = "available";
    }
  }
  return next;
}

const BulkAcceptNewOrdersModal: FC<BulkAcceptNewOrdersModalProps> = ({
  open,
  onClose,
  orderIds,
  factoryId,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const factoryName = useAppSelector((state) => state.auth.contact?.name) || "-";

  const [loadingDetail, setLoadingDetail] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [exporting, setExporting] = useState<"excel" | "pdf" | null>(null);
  const [productGroups, setProductGroups] = useState<BulkAcceptProductGroup[]>([]);
  const [orderCodeById, setOrderCodeById] = useState<Record<string, string>>({});
  /** order_id เฉพาะที่เป็น COD ในรายการที่เลือก — ใช้เช็ควงเงินเครดิต COD (ตัวอื่นไม่เกี่ยว) */
  const [codOrderIds, setCodOrderIds] = useState<string[]>([]);
  /** สถานะจำหน่ายต่อแถว SKU — default พร้อมจำหน่าย (ไม่บันทึก DB) */
  const [skuAvailability, setSkuAvailability] = useState<Record<string, SkuSaleStatus>>(
    {}
  );
  const [batchProgress, setBatchProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);
  const [failedAfterSubmit, setFailedAfterSubmit] = useState<
    { order_id: string; message: string }[] | null
  >(null);

  const uniqueSelectedOrderIds = useMemo(
    () =>
      Array.from(new Set(orderIds.map((id) => String(id).trim()).filter(Boolean))),
    [orderIds]
  );

  const orderCount = uniqueSelectedOrderIds.length;

  const hasFailures = (failedAfterSubmit?.length ?? 0) > 0;

  /** จำนวน SKU หลังรวมยอด (แถว SKU ในตารางสรุป) */
  const aggregatedSkuCount = useMemo(
    () => productGroups.reduce((n, g) => n + g.skus.length, 0),
    [productGroups]
  );

  /** ยอดรวมจำนวนชิ้นที่สั่ง (รวมทุก SKU หลังรวมยอด) */
  const totalPieces = useMemo(
    () => productGroups.reduce((n, g) => n + g.total_qty, 0),
    [productGroups]
  );

  const isSkuUnavailable = useCallback(
    (skuLineKey: string) => (skuAvailability[skuLineKey] ?? "available") === "unavailable",
    [skuAvailability]
  );

  const excludedOrderIds = useMemo(() => {
    const set = new Set<string>();
    for (const g of productGroups) {
      for (const s of g.skus) {
        if (!isSkuUnavailable(s.key)) continue;
        for (const oid of s.orderIds) {
          set.add(oid);
        }
      }
    }
    return Array.from(set);
  }, [productGroups, isSkuUnavailable]);

  const orderIdsToAccept = useMemo(() => {
    const excluded = new Set(excludedOrderIds);
    return uniqueSelectedOrderIds.filter((id) => !excluded.has(id));
  }, [uniqueSelectedOrderIds, excludedOrderIds]);

  // COD ที่จะรับจริง (ตัดใบที่ SKU ไม่พร้อมออก) — เช็ค/แสดงเครดิตเฉพาะชุดนี้
  // ไม่นับใบที่ถูกตัดออก กันบล็อกทั้งชุดเพราะใบที่ไม่ได้รับอยู่แล้ว
  const codOrderIdsToAccept = useMemo(() => {
    const excluded = new Set(excludedOrderIds);
    return codOrderIds.filter((id) => !excluded.has(id));
  }, [codOrderIds, excludedOrderIds]);

  // COD: ก่อนยืนยันรับคำสั่งซื้อ ต้องเช็ควงเงินเครดิต COD ให้พอก่อน — ปุ่มยืนยัน default เป็น
  // disabled ไว้ก่อนจนกว่าจะตรวจสอบผ่าน (ระหว่างโหลด/ไม่พอ ก็ยัง disabled อยู่) ไม่มีผลถ้าไม่มีออเดอร์ COD เลย
  const { data: codCreditInfo, isLoading: codCreditLoading } = useFactoryCodCreditInfo(
    { orderIds: codOrderIdsToAccept },
    { enabled: codOrderIdsToAccept.length > 0 }
  );
  const codCreditBlocksAccept =
    codOrderIdsToAccept.length > 0 && (codCreditLoading || codCreditInfo?.sufficient !== true);

  const setSkuSaleStatus = useCallback((skuLineKey: string, status: SkuSaleStatus) => {
    setSkuAvailability((prev) => ({ ...prev, [skuLineKey]: status }));
  }, []);

  const tableRows: SummaryTableRow[] = useMemo(() => {
    const rows: SummaryTableRow[] = [];
    let skuRank = 0;
    productGroups.forEach((g) => {
      rows.push({
        key: `p-${g.product_id}`,
        isProduct: true,
        rankDisplay: "",
        image: g.product_image_url,
        nameText: g.product_name,
        qtyDisplay: "",
      });
      g.skus.forEach((s) => {
        skuRank += 1;
        rows.push({
          key: s.key,
          isProduct: false,
          rankDisplay: String(skuRank),
          image: s.image_url,
          nameText: s.label,
          qtyDisplay: String(s.qty),
        });
      });
    });
    return rows;
  }, [productGroups]);

  const summaryColumns: ColumnsType<SummaryTableRow> = useMemo(
    () => [
      {
        title: "ลำดับ",
        key: "rank",
        width: 56,
        align: "center",
        render: (_: unknown, row) => {
          if (row.isProduct) {
            return {
              children: (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    justifyContent: "flex-start",
                    width: "100%",
                    textAlign: "left",
                  }}
                >
                  <Image
                    src={row.image || noImage}
                    alt=""
                    width={32}
                    height={32}
                    style={{ objectFit: "cover", borderRadius: 4, flexShrink: 0 }}
                    preview={false}
                  />
                  <Typography.Text
                    ellipsis
                    style={{ flex: 1, minWidth: 0, fontSize: BULK_ACCEPT_SUMMARY_FONT_SIZE }}
                  >
                    {row.nameText}
                  </Typography.Text>
                </div>
              ),
              props: {
                colSpan: 3,
                style: { textAlign: "left", verticalAlign: "middle" },
              },
            };
          }
          return (
            <Typography.Text style={{ fontSize: BULK_ACCEPT_SUMMARY_FONT_SIZE }}>
              {row.rankDisplay}
            </Typography.Text>
          );
        },
      },
      {
        title: "รูป",
        key: "image",
        width: 56,
        align: "center",
        render: (_: unknown, row) => {
          if (row.isProduct) {
            return { children: null, props: { colSpan: 0 } };
          }
          return (
            <Image
              src={row.image || noImage}
              alt=""
              width={32}
              height={32}
              style={{ objectFit: "cover", borderRadius: 4 }}
              preview={false}
            />
          );
        },
      },
      {
        title: "ชื่อสินค้า",
        key: "name",
        ellipsis: true,
        render: (_: unknown, row) => {
          if (row.isProduct) {
            return { children: null, props: { colSpan: 0 } };
          }
          return (
            <Typography.Text
              type={isSkuUnavailable(row.key) ? "secondary" : undefined}
              style={{
                paddingLeft: 8,
                display: "block",
                fontSize: BULK_ACCEPT_SUMMARY_FONT_SIZE,
              }}
            >
              {row.nameText}
            </Typography.Text>
          );
        },
      },
      {
        title: "จำนวน",
        key: "qty",
        width: 72,
        align: "right",
        render: (_: unknown, row) => {
          if (row.isProduct) {
            return null;
          }
          return (
            <Typography.Text
              type={isSkuUnavailable(row.key) ? "secondary" : undefined}
              style={{ fontSize: BULK_ACCEPT_SUMMARY_FONT_SIZE }}
            >
              {row.qtyDisplay}
            </Typography.Text>
          );
        },
      },
      {
        title: "สถานะ",
        key: "status",
        width: 118,
        align: "center",
        render: (_: unknown, row) => {
          if (row.isProduct) {
            return null;
          }
          const value = skuAvailability[row.key] ?? "available";
          return (
            <SkuStatusSelect
              size="small"
              style={{ width: 104 }}
              className={
                value === "unavailable" ? "bulk-accept-sku-status-select-when-unavailable" : undefined
              }
              popupMatchSelectWidth={false}
              popupClassName={SKU_STATUS_SELECT_POPUP_CLASS}
              disabled={submitting}
              value={value}
              options={SKU_STATUS_OPTIONS}
              onChange={(v) => setSkuSaleStatus(row.key, v as SkuSaleStatus)}
            />
          );
        },
      },
    ],
    [isSkuUnavailable, skuAvailability, submitting, setSkuSaleStatus]
  );

  const loadDetails = async () => {
    if (!factoryId || orderIds.length === 0) {
      setProductGroups([]);
      setOrderCodeById({});
      setSkuAvailability({});
      setCodOrderIds([]);
      return;
    }
    setLoadingDetail(true);
    try {
      const uniqueOrderIds = uniqueSelectedOrderIds;
      const allOrders: GetOrderDetailData[] = [];
      const codeMap: Record<string, string> = {};

      for (let i = 0; i < uniqueOrderIds.length; i += DETAIL_CHUNK) {
        const chunk = uniqueOrderIds.slice(i, i + DETAIL_CHUNK);
        const res = await getOrderDetail({
          key_type: "order_id",
          group_by: "factory",
          items: chunk,
          factory_id: factoryId,
        });
        if (res.status === "success" && Array.isArray(res.data)) {
          const seen = new Set(allOrders.map((o) => o.order_id));
          for (const ord of res.data as GetOrderDetailData[]) {
            if (!seen.has(ord.order_id)) {
              seen.add(ord.order_id);
              allOrders.push(ord);
            }
            const code = String(ord.order_code ?? "").trim();
            if (code) {
              codeMap[ord.order_id] = code;
            }
          }
        }
      }

      setCodOrderIds(allOrders.filter((o) => o.order_payment_type === "COD").map((o) => o.order_id));

      type InnerSku = {
        label: string;
        qty: number;
        image_url?: string;
        orderIds: Set<string>;
      };
      type Inner = {
        product_name: string;
        product_image?: string;
        skus: Map<string, InnerSku>;
      };
      const productMap = new Map<string, Inner>();

      for (const ord of allOrders) {
        const items = ord.items ?? [];
        for (const it of items) {
          const productName = String(it.product_name ?? "").trim() || "สินค้า";
          const pid =
            String(it.product_id ?? "").trim() ||
            `name:${productName}`;
          const skuId = String(it.sku_id ?? "").trim();
          const skuLabel =
            String(it.sku_description ?? it.sku_name ?? "").trim() ||
            skuId ||
            "-";
          const skuKey =
            skuId ||
            String(it.price_id ?? "").trim() ||
            `lbl:${skuLabel}`;
          // จำนวนชิ้นของ SKU ในออเดอร์นี้ — ต้องใช้ sku_cnt (จาก API = item.qty)
          // ห้ามใช้ cnt_order: ใน getOrderDetail ถูกเซ็ตเป็น 1 เสมอ (= นับเป็น 1 บรรทัดออเดอร์ ไม่ใช่จำนวนชิ้น)
          const qty = Number(it.sku_cnt ?? it.qty ?? 0) || 0;
          const skuThumb =
            String(it.sku_image || it.image_url || "").trim() ||
            String(it.product_image || "").trim();
          const productImg = String(it.product_image || "").trim();

          let inner = productMap.get(pid);
          if (!inner) {
            inner = { product_name: productName, skus: new Map() };
            productMap.set(pid, inner);
          }
          if (productName && inner.product_name !== productName) {
            inner.product_name = productName;
          }
          if (productImg && !inner.product_image) {
            inner.product_image = productImg;
          }

          const prevSku = inner.skus.get(skuKey);
          if (prevSku) {
            prevSku.qty += qty;
            prevSku.orderIds.add(ord.order_id);
            if (!prevSku.image_url && skuThumb) prevSku.image_url = skuThumb;
          } else {
            inner.skus.set(skuKey, {
              label: skuLabel,
              qty,
              image_url: skuThumb || undefined,
              orderIds: new Set([ord.order_id]),
            });
          }
        }
      }

      const groups: BulkAcceptProductGroup[] = Array.from(
        productMap.entries()
      ).map(([product_id, v]) => {
        const skus: BulkAcceptSkuLine[] = Array.from(v.skus.entries()).map(
          ([skuKey, s]) => ({
            key: `${product_id}-${skuKey}`,
            label: s.label,
            qty: s.qty,
            image_url: s.image_url,
            orderIds: Array.from(s.orderIds),
          })
        );
        skus.sort((a, b) => b.qty - a.qty);
        const total_qty = skus.reduce((acc, s) => acc + s.qty, 0);
        const product_image_url =
          v.product_image ||
          skus.find((s) => String(s.image_url || "").trim())?.image_url;
        return {
          product_id,
          product_name: v.product_name,
          total_qty,
          product_image_url,
          skus,
        };
      });
      groups.sort((a, b) => b.total_qty - a.total_qty);
      setOrderCodeById(codeMap);
      setProductGroups(groups);
      setSkuAvailability(buildDefaultSkuAvailability(groups));
    } catch (e: unknown) {
      console.error(e);
      message.error("โหลดรายละเอียดคำสั่งซื้อไม่สำเร็จ");
      setProductGroups([]);
      setOrderCodeById({});
      setSkuAvailability({});
    } finally {
      setLoadingDetail(false);
    }
  };

  useEffect(() => {
    if (open) {
      setFailedAfterSubmit(null);
      setBatchProgress(null);
      setSkuAvailability({});
      void loadDetails();
    } else {
      setProductGroups([]);
      setOrderCodeById({});
      setSkuAvailability({});
      setFailedAfterSubmit(null);
      setBatchProgress(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load when open / ids change
  }, [open, orderIds.join(","), factoryId]);

  const runBatchAccept = async (
    ids: string[],
    options?: { skippedByUnavailable?: number }
  ) => {
    const uniqueIds = Array.from(
      new Set(ids.map((id) => String(id).trim()).filter(Boolean))
    );
    if (uniqueIds.length === 0) return;
    setSubmitting(true);
    setFailedAfterSubmit(null);
    const failedItems: { order_id: string; message: string }[] = [];
    let okCount = 0;
    const totalChunks = Math.ceil(uniqueIds.length / BATCH_CONFIRM_MAX);
    const skipped = options?.skippedByUnavailable ?? 0;
    try {
      for (let i = 0, chunkIndex = 0; i < uniqueIds.length; i += BATCH_CONFIRM_MAX, chunkIndex++) {
        const chunk = uniqueIds.slice(i, i + BATCH_CONFIRM_MAX);
        setBatchProgress({
          current: chunkIndex + 1,
          total: totalChunks,
        });
        try {
          const res = await confirmManualPaymentBatch({
            order_ids: chunk,
            process: "confirm_order",
            payment_list: [],
          });
          if (res.status !== "success" || !res.data) {
            const fallback =
              typeof res.error_message === "string" && res.error_message
                ? res.error_message
                : "ยังรับคำสั่งซื้อไม่ได้ในครั้งนี้ กรุณาลองใหม่";
            chunk.forEach((order_id) =>
              failedItems.push({ order_id, message: fallback })
            );
            continue;
          }
          okCount += res.data.succeeded?.length ?? 0;
          if (Array.isArray(res.data.failed)) {
            failedItems.push(...res.data.failed);
          }
        } catch (e: unknown) {
          const msg =
            e &&
            typeof e === "object" &&
            "message" in e &&
            typeof (e as { message: unknown }).message === "string"
              ? String((e as { message: string }).message)
              : "เชื่อมต่อไม่ได้หรือระบบขัดข้องชั่วคราว กรุณาลองใหม่";
          chunk.forEach((order_id) =>
            failedItems.push({ order_id, message: msg })
          );
        }
      }

      await queryClient.refetchQueries({
        queryKey: ["orderDataNewOrder"],
        type: "active",
      });
      onSuccess();

      if (failedItems.length === 0) {
        const skipNote =
          skipped > 0 ? ` (ข้าม ${skipped} รายการที่มีสินค้าไม่พร้อมจำหน่าย)` : "";
        message.success(
          `รับคำสั่งซื้อครบทุกรายการแล้ว (${uniqueIds.length} รายการ)${skipNote}`
        );
        setFailedAfterSubmit(null);
        onClose();
      } else if (okCount === 0) {
        message.error(
          `ยังรับคำสั่งซื้อไม่ได้ในครั้งนี้ (${failedItems.length} รายการ) — ${
            failedItems[0]?.message ?? "กรุณาลองใหม่"
          }`
        );
        setFailedAfterSubmit(failedItems);
      } else {
        message.warning(
          `รับคำสั่งซื้อได้ ${okCount} จาก ${uniqueIds.length} รายการ — มี ${failedItems.length} รายการที่ยังรับไม่ได้ ดูรายละเอียดด้านล่าง`
        );
        setFailedAfterSubmit(failedItems);
      }
    } finally {
      setSubmitting(false);
      setBatchProgress(null);
    }
  };

  const handleConfirm = () => {
    if (orderIdsToAccept.length === 0) {
      message.warning(
        "ไม่มีคำสั่งซื้อที่จะรับ — ทุกรายการถูกข้ามเพราะมีสินค้าไม่พร้อมจำหน่าย หรือยังไม่ได้เลือกคำสั่งซื้อ"
      );
      return;
    }
    const skipped = excludedOrderIds.length;
    void runBatchAccept(orderIdsToAccept, { skippedByUnavailable: skipped });
  };

  const handleRetryFailed = () => {
    if (!failedAfterSubmit?.length) return;
    const ids = failedAfterSubmit.map((f) => f.order_id);
    void runBatchAccept(ids);
  };

  const handleCloseAfterFailure = () => {
    setFailedAfterSubmit(null);
    onClose();
  };

  const handleModalCancel = () => {
    if (submitting) return;
    if (hasFailures) handleCloseAfterFailure();
    else onClose();
  };

  const acceptCountLabel =
    excludedOrderIds.length > 0
      ? `ยืนยันรับคำสั่งซื้อ (${orderIdsToAccept.length} รายการ)`
      : "ยืนยันรับคำสั่งซื้อ";

  const canExport = !loadingDetail && productGroups.length > 0;

  const buildExportPayload = useCallback(
    () =>
      buildBulkAcceptExportPayload({
        factoryName,
        productGroups,
        skuAvailability,
        orderCount,
        skuCount: aggregatedSkuCount,
        totalPieces,
        readyToConfirmCount: orderIdsToAccept.length,
        notReadyToConfirmCount: excludedOrderIds.length,
      }),
    [
      factoryName,
      productGroups,
      skuAvailability,
      orderCount,
      aggregatedSkuCount,
      totalPieces,
      orderIdsToAccept.length,
      excludedOrderIds.length,
    ]
  );

  const handleExportExcel = useCallback(() => {
    if (!canExport) {
      message.warning("ไม่มีข้อมูลให้ส่งออก");
      return;
    }
    setExporting("excel");
    try {
      exportBulkAcceptSummaryExcel(buildExportPayload());
      message.success("ส่งออกไฟล์ Excel สำเร็จ");
    } catch (e) {
      console.error(e);
      message.error("ไม่สามารถส่งออกไฟล์ Excel ได้");
    } finally {
      setExporting(null);
    }
  }, [buildExportPayload, canExport]);

  const handleExportPdf = useCallback(async () => {
    if (!canExport) {
      message.warning("ไม่มีข้อมูลให้ส่งออก");
      return;
    }
    setExporting("pdf");
    try {
      await createBulkAcceptSummaryPdf(buildExportPayload());
      message.success("ส่งออกไฟล์ PDF สำเร็จ");
    } catch (e) {
      console.error(e);
      message.error("ไม่สามารถส่งออกไฟล์ PDF ได้");
    } finally {
      setExporting(null);
    }
  }, [buildExportPayload, canExport]);

  return (
    <Modal
      rootClassName={BULK_ACCEPT_MODAL_ROOT_CLASS}
      title="ยืนยันรับคำสั่งซื้อ"
      open={open}
      onCancel={handleModalCancel}
      width={720}
      closable={!submitting}
      maskClosable={!submitting}
      footer={
        hasFailures
          ? [
              <Button key="close" onClick={handleCloseAfterFailure} disabled={submitting}>
                ปิด
              </Button>,
              <Button
                key="retry"
                type="primary"
                loading={submitting}
                disabled={submitting}
                onClick={() => void handleRetryFailed()}
              >
                ลองรับอีกครั้งเฉพาะที่ไม่สำเร็จ ({failedAfterSubmit!.length} รายการ)
              </Button>,
            ]
          : [
              <Button key="cancel" onClick={onClose} disabled={submitting}>
                ยกเลิก
              </Button>,
              <Button
                key="ok"
                type="primary"
                loading={submitting}
                disabled={
                  loadingDetail ||
                  orderCount === 0 ||
                  orderIdsToAccept.length === 0 ||
                  codCreditBlocksAccept
                }
                onClick={() => void handleConfirm()}
              >
                {acceptCountLabel}
              </Button>,
            ]
      }
      destroyOnClose
      bodyStyle={{
        paddingTop: 16,
        paddingBottom: 20,
        paddingLeft: 32,
        paddingRight: 32,
      }}
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Global styles={bulkAcceptModalGlobalStyles} />

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <Typography.Text style={{ fontSize: BULK_ACCEPT_SUMMARY_FONT_SIZE }}>
            สรุปรายการคำสั่งซื้อ
          </Typography.Text>
          {canExport && !hasFailures && (
            <Space wrap size="small">
              <Button
                size="small"
                style={{ fontSize: BULK_ACCEPT_SUMMARY_FONT_SIZE, fontWeight: 400 }}
                icon={<FileExcelOutlined style={{ color: "#52c41a" }} />}
                loading={exporting === "excel"}
                disabled={submitting || exporting !== null}
                onClick={handleExportExcel}
              >
                ส่งออก Excel
              </Button>
              <Button
                size="small"
                style={{ fontSize: BULK_ACCEPT_SUMMARY_FONT_SIZE, fontWeight: 400 }}
                icon={<FilePdfOutlined style={{ color: "#ff4d4f" }} />}
                loading={exporting === "pdf"}
                disabled={submitting || exporting !== null}
                onClick={() => void handleExportPdf()}
              >
                ส่งออก PDF
              </Button>
            </Space>
          )}
        </div>

        <div
          style={{
            border: "1px solid #f0f0f0",
            borderRadius: 8,
            padding: "14px 16px",
            backgroundColor: "#fafafa",
          }}
        >
          <div
            style={{
              ...BULK_ACCEPT_QTY_SUMMARY_GRID_STYLE,
              fontSize: BULK_ACCEPT_SUMMARY_FONT_SIZE,
            }}
          >
            <Typography.Text>คำสั่งซื้อ</Typography.Text>
            <Typography.Text type="secondary">จำนวน</Typography.Text>
            <Typography.Text
              style={{
                fontSize: BULK_ACCEPT_SUMMARY_FONT_SIZE,
                textAlign: "right",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {orderCount}
            </Typography.Text>
            <Typography.Text>รายการ</Typography.Text>

            <Typography.Text>รายการสินค้า (ตัวเลือกสินค้า)</Typography.Text>
            <Typography.Text type="secondary">จำนวน</Typography.Text>
            <Typography.Text
              style={{
                fontSize: BULK_ACCEPT_SUMMARY_FONT_SIZE,
                textAlign: "right",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {aggregatedSkuCount}
            </Typography.Text>
            <Typography.Text>SKU</Typography.Text>

            <Typography.Text>การสั่งซื้อ</Typography.Text>
            <Typography.Text type="secondary">จำนวน</Typography.Text>
            <Typography.Text
              style={{
                fontSize: BULK_ACCEPT_SUMMARY_FONT_SIZE,
                textAlign: "right",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {totalPieces}
            </Typography.Text>
            <Typography.Text>ชิ้น</Typography.Text>

            {excludedOrderIds.length > 0 && (
              <>
                <Typography.Text>คำสั่งซื้อที่พร้อมยืนยัน</Typography.Text>
                <Typography.Text type="secondary">จำนวน</Typography.Text>
                <Typography.Text
                  style={{
                    fontSize: BULK_ACCEPT_SUMMARY_FONT_SIZE,
                    textAlign: "right",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {orderIdsToAccept.length}
                </Typography.Text>
                <Typography.Text>รายการ</Typography.Text>

                <Typography.Text>คำสั่งซื้อที่ไม่พร้อมยืนยัน</Typography.Text>
                <Typography.Text type="secondary">จำนวน</Typography.Text>
                <Typography.Text
                  type="danger"
                  style={{
                    fontSize: BULK_ACCEPT_SUMMARY_FONT_SIZE,
                    textAlign: "right",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {excludedOrderIds.length}
                </Typography.Text>
                <Typography.Text>รายการ</Typography.Text>
              </>
            )}
          </div>
        </div>

        <CodCreditInfoBulk
          orderIds={codOrderIdsToAccept}
          enabled={!loadingDetail}
          fontSize={BULK_ACCEPT_SUMMARY_FONT_SIZE}
        />

        {batchProgress && (
          <Typography.Text type="secondary">
            กำลังรับคำสั่งซื้อ… ชุดที่ {batchProgress.current} จาก {batchProgress.total}
          </Typography.Text>
        )}

        {hasFailures && (
          <Alert
            type="warning"
            showIcon
            message="มีบางรายการที่ยังรับไม่ได้"
            description={
              <div
                style={{
                  maxHeight: 200,
                  overflowY: "auto",
                  marginTop: 8,
                }}
              >
                <List
                  size="small"
                  dataSource={failedAfterSubmit!}
                  renderItem={(row) => (
                    <List.Item>
                      <div>
                        <Typography.Text>{row.order_id}</Typography.Text>
                        <div>
                          <Typography.Text type="secondary">{row.message}</Typography.Text>
                        </div>
                      </div>
                    </List.Item>
                  )}
                />
              </div>
            }
          />
        )}

        {loadingDetail ? (
          <div style={{ textAlign: "center", padding: 24 }}>
            <Spin tip="กำลังเตรียมข้อมูลสินค้า…" />
          </div>
        ) : (
          <>
            <Typography.Text
              type="secondary"
              style={{ display: "block", fontSize: BULK_ACCEPT_SUMMARY_FONT_SIZE }}
            >
              รายละเอียดคำสั่งซื้อ
            </Typography.Text>
            <div
              className={BULK_ACCEPT_DETAIL_TABLE_CLASS}
              style={{
                maxHeight: 360,
                overflowY: "auto",
                border: "1px solid #f0f0f0",
                borderRadius: 8,
              }}
            >
              <Table<SummaryTableRow>
                size="small"
                bordered
                pagination={false}
                rowKey="key"
                scroll={{ x: 480 }}
                columns={summaryColumns}
                dataSource={tableRows}
                locale={{ emptyText: "ไม่พบรายการสินค้า" }}
                rowClassName={(row) => {
                  if (row.isProduct) return "bulk-accept-row-product";
                  return isSkuUnavailable(row.key)
                    ? "bulk-accept-row-sku bulk-accept-row-unavailable"
                    : "bulk-accept-row-sku";
                }}
              />
            </div>
            <div
              style={{
                marginTop: 12,
                padding: "12px 16px",
                borderRadius: 8,
                border: "1px solid #ffe58f",
                backgroundColor: "#fffbe6",
                boxShadow: "0 1px 2px rgba(0, 0, 0, 0.03)",
              }}
            >
              <Typography.Text style={{ fontSize: BULK_ACCEPT_SUMMARY_FONT_SIZE }}>
                โปรดตรวจสอบรายการสินค้าแต่ละ SKU 
                หากรายการใดไม่พร้อมจำหน่าย ให้เลือก &quot;ไม่พร้อมจำหน่าย&quot; ด้านหลังรายการนั้น ๆ 
                เมื่อกดปุ่ม &quot;ยืนยันรับคำสั่งซื้อ&quot; คำสั่งซื้อที่มี SKU นั้นจะไม่ถูกยืนยันรับในครั้งนี้ 
              </Typography.Text>
            </div>
          </>
        )}
      </Space>
    </Modal>
  );
};

export default BulkAcceptNewOrdersModal;
